/**
 * Shim for Apollo Client 4: re-export Query as a render-prop component using useQuery.
 * Apollo Client 4 does not export @apollo/client/react/components; use this instead.
 */
import React from 'react';
import { useQuery } from '@apollo/client/react';
import type { OperationVariables } from '@apollo/client';
import type { DocumentNode } from 'graphql';

/** Options type derived from useQuery (replaces deprecated QueryHookOptions). */
type UseQueryOptions<TData, TVariables extends OperationVariables> = NonNullable<
  Parameters<typeof useQuery<TData, TVariables>>[1]
>;

/** Result type from useQuery for the render-prop callback. */
type UseQueryResult<TData, TVariables extends OperationVariables> = ReturnType<
  typeof useQuery<TData, TVariables>
>;

export type QueryComponentProps<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables
> = Omit<UseQueryOptions<TData, TVariables>, 'query'> & {
  query: DocumentNode;
  variables?: TVariables;
  fetchPolicy?:
    | 'cache-first'
    | 'cache-and-network'
    | 'cache-only'
    | 'no-cache'
    | 'network-only'
    | 'standby';
  context?: Record<string, unknown>;
  children: (result: UseQueryResult<TData, TVariables>) => React.ReactNode;
};

export function Query<TData = unknown, TVariables extends OperationVariables = OperationVariables>({
  query,
  children,
  ...options
}: QueryComponentProps<TData, TVariables>): React.ReactElement | null {
  const result = useQuery<TData, TVariables>(query, options as UseQueryOptions<TData, TVariables>);
  return <>{children(result as UseQueryResult<TData, TVariables>)}</>;
}
